<template>
  <div class="relative" ref="bellRef">
    <!-- Bell button -->
    <button
      @click.stop="toggleDropdown"
      class="relative p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
      aria-label="Notifications"
    >
      <!-- Bell icon -->
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      <!-- Unread badge -->
      <span
        v-if="store.unreadCount > 0"
        class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full leading-none"
      >
        {{ store.unreadCount > 99 ? '99+' : store.unreadCount }}
      </span>
    </button>

    <!-- Dropdown panel -->
    <Teleport to="body">
      <!-- Mobile backdrop -->
      <div
        v-if="open"
        class="fixed inset-0 z-[295] bg-black/20"
        @click="open = false"
      />
      <div
        v-if="open"
        :style="dropdownStyle"
        class="fixed z-[300] bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col overflow-hidden"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span class="font-semibold text-gray-900 text-sm">Notifications</span>
          <div class="flex items-center gap-3">
            <button
              v-if="store.unreadCount > 0"
              @click="handleMarkAllRead"
              class="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Mark all read
            </button>
            <button
              v-if="store.notifications.length > 0"
              @click="handleClearAll"
              class="text-xs text-gray-400 hover:text-red-500 font-medium transition-colors"
            >
              Clear all
            </button>
          </div>
        </div>

        <!-- Notification list -->
        <div class="overflow-y-auto max-h-80 divide-y divide-gray-100">
          <template v-if="store.isLoading && store.notifications.length === 0">
            <div class="px-4 py-8 text-center text-sm text-gray-400">Loading…</div>
          </template>
          <template v-else-if="store.notifications.length === 0">
            <div class="px-4 py-8 text-center text-sm text-gray-400">No notifications yet</div>
          </template>
          <NotificationItem
            v-for="notification in store.notifications"
            :key="notification.id"
            :notification="notification"
            :show-actions="true"
          />
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-100 px-4 py-2">
          <router-link
            to="/user?tab=notifications"
            @click="open = false"
            class="block text-center text-xs text-primary-600 hover:text-primary-700 font-medium py-1 transition-colors"
          >
            View all notifications
          </router-link>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotificationStore } from '../../stores/notifications'
import NotificationItem from './NotificationItem.vue'

const store = useNotificationStore()
const open = ref(false)
const bellRef = ref<HTMLElement | null>(null)

// Position the panel: centered on mobile, anchored below bell on desktop
const dropdownStyle = computed(() => {
  if (!bellRef.value) return {}
  const rect = bellRef.value.getBoundingClientRect()
  const vw = window.innerWidth

  if (vw < 640) {
    // Mobile: center horizontally, just below the nav bar
    const padding = 16
    const panelWidth = Math.min(320, vw - padding * 2)
    return {
      top: `${rect.bottom + 8}px`,
      left: `${(vw - panelWidth) / 2}px`,
      width: `${panelWidth}px`
    }
  }

  // Desktop: right-aligned below the bell button
  return {
    top: `${rect.bottom + 8}px`,
    right: `${vw - rect.right}px`,
    width: '320px'
  }
})

async function toggleDropdown() {
  open.value = !open.value
  if (open.value) {
    await Promise.all([
      store.fetchLatestNotifications(),
      store.fetchPendingInvitations()
    ])
  }
}

async function handleMarkAllRead() {
  await store.markAllAsRead()
}

async function handleClearAll() {
  await store.clearAllNotifications()
}

function handleClickOutside(e: MouseEvent) {
  if (open.value && bellRef.value && !bellRef.value.contains(e.target as Node)) {
    open.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>
